import { ref, watch } from "vue";
import { agentSessionScope } from "@/shared/agent-session-scope";
import { type AgentSession, useSessions } from "../api/use-agents";

/**
 * A conversa em que cada agente está no meio.
 *
 * `StreamingChat` emite `sessionStarted` e a tela de Agentes não escutava, então
 * a cada ida e volta o componente remontava sem sessão e começava outra. Tudo o
 * que se tinha dito ao construtor ficava órfão — não apagado, os turnos estão
 * todos no servidor, mas inalcançável da tela que os produziu.
 *
 * `sessionStorage` e não um `ref`: um `ref` sobrevive à navegação dentro do app e
 * não a um recarregamento, e pedir a um construtor que reexplique o que ele
 * estava fazendo pela metade é exatamente o momento em que se desiste dele. Por
 * AGENTE, porque dois agentes são duas conversas.
 */
function storageKey(): string {
	return `benvox.agent.sessions:${agentSessionScope.value ?? "unavailable"}`;
}

export function useRememberedSession() {
	const current = ref<Record<string, string>>(read());
	const { data: sessions } = useSessions();
	watch(
		agentSessionScope,
		() => {
			current.value = read();
		},
		{ flush: "sync" },
	);
	// Uma conversa lembrada que a primeira lista do servidor não tem (apagada, de
	// outra conta) sai da memória da aba; sem isto, cada recarregamento a pedia de
	// novo e levava 404. Só a primeira: uma lista em voo durante o primeiro turno
	// não traz a conversa que acabou de nascer.
	let pruned = false;
	watch(agentSessionScope, () => (pruned = false), { flush: "sync" });
	watch(
		sessions,
		(list) => {
			if (!list || pruned) return;
			pruned = true;
			const kept = Object.fromEntries(
				Object.entries(current.value).filter(([, id]) =>
					list.some((s) => s.id === id && !s.subject),
				),
			);
			if (Object.keys(kept).length !== Object.keys(current.value).length) write(kept);
		},
		{ immediate: true },
	);

	function sessionFor(code: string | null): string | null {
		if (!code) return null;
		const id = current.value[code] ?? null;
		if (!sessions.value) return id;
		const found = sessions.value?.find((session) => session.id === id);
		return found && !found.subject ? id : null;
	}

	function remember(code: string, id: string): void {
		write({ ...current.value, [code]: id });
	}

	/** Apagar a que estava aberta faz a próxima mensagem começar uma nova. */
	function forget(code: string | null, id: string): void {
		if (!code || current.value[code] !== id) return;
		const { [code]: _gone, ...rest } = current.value;
		write(rest);
	}

	/** As conversas que já existem deste agente, na ordem em que o servidor as deu. */
	function sessionsOf(code: string | null): readonly AgentSession[] {
		return code
			? (sessions.value ?? []).filter((session) => session.agentCode === code && !session.subject)
			: [];
	}

	function write(next: Record<string, string>): void {
		current.value = next;
		try {
			sessionStorage.setItem(storageKey(), JSON.stringify(next));
		} catch {
			// Navegação privada ou cota cheia. A linha continua funcionando nesta visita;
			// ela só não estará aqui depois de um recarregamento — que é o que fazia antes.
		}
	}

	return { sessionFor, remember, forget, sessionsOf };
}

function read(): Record<string, string> {
	try {
		const raw = sessionStorage.getItem(storageKey());
		const parsed = raw ? (JSON.parse(raw) as unknown) : null;
		return parsed && typeof parsed === "object" ? (parsed as Record<string, string>) : {};
	} catch {
		return {};
	}
}
